'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { useSidebarStore } from '@/hooks/useSidebar'
import { userNavItems, type NavItem } from './nav-config'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
import { ScrollArea } from '@/components/ui/scroll-area'
import { UserCircle, ChevronDown, ExternalLink } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

import { useGeneralSettings } from '@/sdk/admin/features/general_settings'
import { useDownloads } from '@/sdk/client/features/downloads'
import { apiClient } from '@/sdk/shared/apiClient'
import { getTenantSlug, getCookie } from '@/sdk/shared/auth/utils'
import type { LucideIcon } from 'lucide-react'

function NavIcon({ icon, className, isActive, isCollapsed }: { icon: LucideIcon | string; className?: string; isActive?: boolean; isCollapsed?: boolean }) {
  if (typeof icon === 'string') {
    const size = isCollapsed ? 25 : 20
    return (
      <img
        src={icon}
        alt=""
        className={cn('shrink-0', className)}
        style={{
          width: size,
          height: size,
          filter: isActive
            ? 'brightness(0) invert(1)'
            : 'brightness(0) invert(1) opacity(0.65)',
          transition: 'filter 0.2s',
        }}
      />
    )
  }
  const Icon = icon
  return <Icon className={cn('h-5 w-5 shrink-0', className)} />
}

export function UserSidebar({ className, forceExpanded = false, onItemClick }: { className?: string; forceExpanded?: boolean; onItemClick?: (href?: string) => void }) {
  const { isCollapsed } = useSidebarStore()
  const collapsed = forceExpanded ? false : isCollapsed

  const tenantSlug = getTenantSlug() || getCookie('tenant_slug') || 'default-tenant'
  const { generalSettings } = useGeneralSettings(tenantSlug)

  const { downloads = [] } = useDownloads()

  const webPlatform = React.useMemo(() => {
    return downloads.find(d => {
      const t = (d.type || '').toLowerCase()
      return t === 'website' || t.includes('web')
    })
  }, [downloads])

  const webPlatformUrl = React.useMemo(() => {
    if (!webPlatform?.download_link) return null
    const link = webPlatform.download_link
    if (link.startsWith('/api/')) {
      const baseUrl = apiClient.getApiURL().replace(/\/$/, '')
      return `${baseUrl}${link}`
    }
    if (!link.startsWith('http://') && !link.startsWith('https://')) {
      return `https://${link}`
    }
    return link
  }, [webPlatform])

  const navItems = React.useMemo(() => {
    return userNavItems
      .filter(item => {
        // Based upon tenant: show Web Platform only if the active tenant has a web platform configured!
        if (item.label === 'Web Platform' || item.href === '/client/web-platform') {
          return !!webPlatformUrl
        }
        return true
      })
      .map(item => {
        if (item.label === 'Web Platform' || item.href === '/client/web-platform') {
          return {
            ...item,
            href: webPlatformUrl || item.href,
            target: '_blank',
            external: true,
          }
        }
        return item
      })
  }, [webPlatformUrl])

  return (
    <TooltipProvider delay={0}>
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 70 : 260 }}
        transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
        className={cn(
          'bg-sidebar-bg text-sidebar-fg flex flex-col z-20 overflow-hidden shrink-0',
          forceExpanded
            ? 'rounded-[20px] my-2 ml-2 h-[calc(100dvh-1rem-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] border border-white/5 shadow-xl'
            : 'rounded-[20px] my-4 ml-4 h-[calc(100dvh-2rem)] border border-white/5 shadow-xl',
          className
        )}
      >
        <div className={cn("flex h-16 items-center shrink-0 overflow-hidden justify-center", collapsed ? "px-2" : "px-4")}>
          {generalSettings?.logoUrl ? (
            <div className="flex items-center justify-center h-full w-full">
              <img
                src={generalSettings.logoUrl}
                alt="Tenant Logo"
                className="object-contain transition-all ease-out"
                style={{
                  maxHeight: collapsed ? '48px' : '58px',
                  maxWidth: collapsed ? '48px' : '220px',
                  transitionDuration: '750ms',
                }}
              />
            </div>
          ) : (
            <>
              <UserCircle className="h-6 w-6 text-primary shrink-0" />
              <AnimatePresence mode="sync">
                {!collapsed && (
                  <motion.span
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2 }}
                    className="ml-3 font-bold text-lg text-foreground font-sans truncate"
                  >
                    Client Portal
                  </motion.span>
                )}
              </AnimatePresence>
            </>
          )}
        </div>
        <div className={cn("shrink-0", collapsed ? "px-3" : "px-6")}>
          <div className="h-[1px] w-full bg-[#1F2937]" />
        </div>

        <ScrollArea className="flex-[1_1_0] overflow-hidden py-3">
          <nav className="space-y-1 px-2">
            {navItems.map((item, idx) => (
              <React.Fragment key={idx}>
                {item.section && !collapsed && (
                  <div className={cn(
                    "px-3 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-sidebar-fg/50 select-none",
                    idx > 0 ? "pt-4" : "pt-1"
                  )}>
                    {item.section}
                  </div>
                )}
                <SidebarItem item={item} isCollapsed={collapsed} onItemClick={onItemClick} />
              </React.Fragment>
            ))}
          </nav>
        </ScrollArea>
      </motion.aside>
    </TooltipProvider>
  )
}

function SidebarItem({ item, isCollapsed, onItemClick }: { item: NavItem; isCollapsed: boolean; onItemClick?: (href?: string) => void }) {
  const pathname = usePathname()
  const hasChildren = item.children && item.children.length > 0
  const [isOpen, setIsOpen] = useState(false)

  if (hasChildren) {
    return (
      <div className="flex flex-col">
        <button
          onClick={() => !isCollapsed && setIsOpen(!isOpen)}
          className={cn(
            'flex w-full items-center justify-between rounded-xl p-2 text-sm font-medium transition-colors text-sidebar-fg hover:bg-white/5 hover:text-white',
            !isCollapsed && 'px-3'
          )}
        >
          <div className="flex items-center">
            <NavIcon icon={item.icon} isActive={false} isCollapsed={isCollapsed} />
            <AnimatePresence mode="sync">
              {!isCollapsed && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="ml-3 truncate whitespace-nowrap overflow-hidden"
                >
                  {item.label}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          {!isCollapsed && (
            <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <ChevronDown className="h-4 w-4" />
            </motion.div>
          )}
        </button>
        <AnimatePresence>
          {isOpen && !isCollapsed && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="space-y-1 pl-9 mt-1">
                {item.children?.map((child, idx) => (
                  <SidebarLink key={idx} item={child} isCollapsed={false} onItemClick={onItemClick} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  return <SidebarLink item={item} isCollapsed={isCollapsed} onItemClick={onItemClick} />
}

function SidebarLink({ item, isCollapsed, onItemClick }: { item: NavItem; isCollapsed: boolean; onItemClick?: (href?: string) => void }) {
  const pathname = usePathname()
  const isExternal = item.target === '_blank' || item.external || (item.href && (item.href.startsWith('http://') || item.href.startsWith('https://')))
  const isActive = item.href && !isExternal ? pathname.startsWith(item.href) : false

  const link = (
    <Link
      href={item.href || '#'}
      prefetch={false}
      target={item.target || (isExternal ? '_blank' : undefined)}
      rel={item.target === '_blank' || isExternal ? 'noopener noreferrer' : undefined}
      onClick={(e) => {
        if (onItemClick) {
          // Mobile context: we navigate via router.push in handleItemClick.
          // Prevent the Link's own navigation to avoid double-push.
          if (!isExternal) e.preventDefault()
          onItemClick(item.href)
        }
      }}
      className={cn(
        'group relative flex items-center rounded-xl p-2 text-sm font-medium transition-all duration-200 cursor-pointer touch-manipulation select-none',
        isActive
          ? 'text-white'
          : 'text-sidebar-fg/70 hover:text-white',
        !isCollapsed && 'px-3'
      )}
      style={isActive ? {
        background: 'linear-gradient(90deg, hsl(var(--primary) / 0.18) 0%, hsl(var(--primary) / 0.06) 100%)',
      } : undefined}
    >
      {/* Left accent bar */}
      {isActive && (
        <span
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full"
          style={{
            height: '60%',
            background: 'hsl(var(--primary))',
            boxShadow: '0 0 10px 3px hsl(var(--primary) / 0.5)',
          }}
        />
      )}
      <NavIcon
        icon={item.icon}
        isActive={isActive}
        isCollapsed={false}
        className={cn(
          'transition-colors',
          isActive ? 'text-white' : 'text-sidebar-fg/60 group-hover:text-white'
        )}
      />
      <AnimatePresence mode="sync">
        {!isCollapsed && (
          <motion.span
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            className={cn(
              'ml-3 truncate whitespace-nowrap overflow-hidden font-semibold flex-1',
              isActive ? 'text-white' : 'font-medium'
            )}
          >
            {item.label}
          </motion.span>
        )}
      </AnimatePresence>
      {isExternal && !isCollapsed && (
        <ExternalLink className="h-3.5 w-3.5 ml-1.5 opacity-40 group-hover:opacity-100 transition-opacity shrink-0" />
      )}
    </Link>
  )

  if (isCollapsed) {
    return (
      <div className="relative w-full flex items-center justify-center">
        {/* Active left accent pill */}
        {isActive && (
          <span
            className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full"
            style={{
              height: '65%',
              background: 'hsl(var(--primary))',
              boxShadow: '0 0 10px 3px hsl(var(--primary) / 0.55)',
            }}
          />
        )}
        <Tooltip>
          <TooltipTrigger
            render={
              <Link
                href={item.href || '#'}
                prefetch={false}
                target={item.target || (isExternal ? '_blank' : undefined)}
                rel={item.target === '_blank' || isExternal ? 'noopener noreferrer' : undefined}
                onClick={(e) => {
                  if (onItemClick) {
                    if (!isExternal) e.preventDefault()
                    onItemClick(item.href)
                  }
                }}
                className={cn(
                  'group relative flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-200 cursor-pointer touch-manipulation select-none',
                  isActive
                    ? 'text-white'
                    : 'text-sidebar-fg/60 hover:text-white hover:bg-white/5'
                )}
                style={isActive ? {
                  background: 'radial-gradient(ellipse at center, hsl(var(--primary) / 0.25) 0%, hsl(var(--primary) / 0.06) 75%)',
                } : undefined}
              >
                <NavIcon
                  icon={item.icon}
                  isActive={isActive}
                  isCollapsed={true}
                  className={cn(
                    'transition-colors',
                    isActive ? 'text-white' : 'text-sidebar-fg/60 group-hover:text-white'
                  )}
                />
              </Link>
            }
          />
          <TooltipContent side="right" className="font-semibold px-3 py-1.5 flex items-center gap-1.5">
            <span>{item.label}</span>
            {isExternal && <ExternalLink className="h-3 w-3 opacity-60" />}
          </TooltipContent>
        </Tooltip>
      </div>
    )
  }

  return link
}